import React, { SyntheticEvent, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, FormControl, FormHelperText, Grid, Modal, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import { Button, ButtonVariantProps, ButtonColorProps } from '../../../components/atoms/Button';
import { PageContainer } from '../../../components/atoms/PageContainer';
import { DateTimePicker } from '../../../components/atoms/DateTimePicker';
import { TextField } from '../../../components/atoms/TextField';
import { DropdownItem } from '../../../components/atoms/SelectMenu';
import { TypographyVariantProps } from '../../../components/atoms/Typography';
import { Snackbar } from '../../../components/atoms/Snackbar';
import { LiveSearchBox, LiveSearchListItem } from '../../../components/atoms/LiveSearchBox';
import { LocationSearchItem } from '../../../components/molecules/LocationSearchItem';
import { PrioritySelectMenu } from '../../../components/molecules/PrioritySelectMenu';
import { AutocompleteTagType, getNewTagsMapped, getSelectedExistingTagsMapped, Tags } from '../../../components/organisms/Tags';
import { Tag, LocationNodeData, NodeType, ActionPermissions, SizeProps, NodePartTypeData, ColorProps, MerchantDamageObjectParcer, InterceptedFormsObject, FormObject, FormFieldType, ModifyStructureFormModifyStructureType, ParameterDataFormParameterType } from '../../../@types';
import { ServiceTypography, ServiceBox, StyledSelectMenu, StyledBox, SwitchGrid, ModeSwitchGrid, StyledEditLocationBox, StyledButton, ContentBox, StructureEditContainer, InteractionsContainer } from './AddService.styles';
import { ServiceObject, PriorityType, ServiceCommonType, ServiceTemplateDataType, ServiceTargetOptions, CustomNodeAttributeType, ServiceStatusCode, ServiceStatusLabel } from '../../../@types/service.type';
import { actionTypes, getHasCodeForNodeSearch, getInitialPriority, priorityTypes, processTypes, reportedFaultTypes, reportedModelTypes, serviceTypeMappings, serviceTypes } from '../../../utils/services';
import { EntityType, FaultCategory, NodeAttributeType, ServiceAttachmentUploadFeature, ServiceCreationRuleTypes, StructureTypeCategory } from '../../../configs/enums';
import { useGetAllTags, useAddTags } from '../../../queries/tags-query';
import { useAddService, useAddServiceBulk, useGetServiceActionProcessTypes } from '../../../queries/service-query';
import {
  useCheckMultipleServiceCreationAccess,
  useChildLocationCountGroupedByNodeType,
  useCreateStructure,
  useGetFilteredNodeAttributesByCustomRange,
  useGetNodeAttributesByCustomRange,
  useGetNodeChildrenByNodeType,
  useGetNodeChildrenByNodeTypeAsEnabled,
  useGetNodeSubTree,
  useGetStructuresByTypeCategoryCodeAndTypeCode,
  useGetSuggestedNodesFromAllocationEngine
} from '../../../queries/structure-query';
import {
  useSearchAllocationNodes,
  useSearchNodeByParentId,
  useSearchNodeWithParts,
  useSearchParts,
  useSubLocationsForCreateServices
} from '../../../queries/live-search-query';
import { emptyFieldValidation, formatDate, isDropDownEmpty, isEmptyString } from '../../../utils/common';
import { getServiceAttributes, isClientEmpty, isHaveError, isRuleExist } from '../../../utils/services';
import { GRID_SPACING } from '../../../configs/ui-constants';
import { LocationSearchSelect } from '../../../components/organisms/LocationSearchSelect';
import { ServiceTemplateList } from '../../../components/organisms/ServiceTemplateList';
import { useGetServiceTemplates } from '../../../queries/service-template-query';
import { EMPTY_FIELD_MESSAGE, NONE_ITEM, RICH_TEXT_EDITOR_EMPTY_TEXT } from '../../../constants/common';
import { useGetCurrentUser, useGetUserSystemPermissions, useUpdateProfileConfig } from '../../../queries/user-query';
import { isUserHasPermission } from '../../../configs/permissions';
import { SAMPLE_PRIORITY_TYPE, SAMPLE_SERVICE, SAMPLE_SERVICE_COMMON_TYPE } from '../../../constants/service.constant';
import { RichTextEditor } from '../../../components/atoms/RichTextEditor';
import { ServiceContactLayout } from '../../../components/templates/ServiceContactLayout';
import { ContactData } from '../../../components/molecules/Contact';
import { MerchantDamagePartSerialMenuData, SearchCode } from '../../../@types/part.type';
import { ContactDataType } from '../../../components/templates/StructureAddressContactLayout';
import { PLATFORM_NAME } from '../../../configs/common';
import { TabHandler } from '../../../handlers/TabHandler';
import { useGetCurrentUserVisibility } from '../../../queries/visibilityRules-query';
import { FormControlLabel, FormControlLabelPlacementProps } from '../../../components/atoms/FormControlLabel';
import { Switch } from '../../../components/atoms/Switch';
import { CloseIcon } from '../../../components/atoms/CloseIcon';
import { AddEditStructureLocationWrapper } from '../../Structures/AddEditStructureLocation/StructureLocationPageWrapper';
import { ClientContractBrandPanel } from '../../../components/molecules/ClientContractBrandPanel';
import { Loader, LoaderColorProps } from '../../../components/atoms/Loader';
import { FormInterceptor } from '../UpdateService/ServiceActionBarFragment/FormInterceptor';
import { FormInterceptorContext, FormInterceptorContextProps } from '../../auth/AxiosInterceptor';
import { getFormIsRepeatableStatus, validateInteractionForms, hasByPassedFields, blockInteractionAvailable, getBlockInteractionMessage, hasFormFieldValueError, updateErrorState } from '../../../utils/interactions';
import { COLORS } from '../../../configs/colors';
import { Folders } from '../../../@types/uploaded-files.type';
import { uploadFiles } from '../../../api/uploadedFiles';
import { useModifyStructuresToggleActive, useSubmitFormsDataWithOriginalPayload } from '../../../queries/interactions-query';
import { useUpdatePartAdditionalData } from '../../../queries/part-query';

export const AddService: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeOnly = true;

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [selectedParentHierarchy, setSelectedParentHierarchy] = useState('');
  const [data, setData] = useState<ServiceObject>(SAMPLE_SERVICE);
  const [selectedTags, setSelectedTags] = useState<(AutocompleteTagType | string)[]>([]);
  const [newTags, setNewTags] = useState<Tag[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [serviceCommonTypes, setServiceCommonTypes] = useState<ServiceCommonType>(SAMPLE_SERVICE_COMMON_TYPE);
  const [queryNodeType, setQueryNodeType] = useState('client');
  const [selectedParentId, setSelectedParentId] = useState('');
  const [clients, setClients] = useState<DropdownItem[]>([]);
  const [contracts, setContracts] = useState<DropdownItem[]>([]);
  const [brands, setBrands] = useState<DropdownItem[]>([]);
  const [nodeAttributes, setNodeAttributes] = useState<[PriorityType]>([SAMPLE_PRIORITY_TYPE]);
  const [parentId, setParentId] = useState('');
  const [brandId, setBrandId] = useState('0');
  const [nodeAttributeTypes, setNodeAttributeTypes] = useState<NodeAttributeType[]>([]);
  const [serviceTypeCode, setServiceTypeCode] = useState('');
  const [templateData, setTemplateData] = useState<ServiceTemplateDataType[]>([]);
  const [focusField, setFocusField] = useState('');
  const [defaultServiceTargetOption, setDefaultTargetOption] = useState<ServiceTargetOptions | null>();
  const [serviceTargetOptions, setServiceTargetOptions] = useState<ServiceTargetOptions[] | null>([]);
  const [selectedClientId, setSelectedClientId] = useState(NaN);
  const [selectedContractId, setSelectedContractId] = useState(NaN);
  const [selectedBrandId, setSelectedBrandId] = useState(NaN);
  const [multiServiceCreationFieldEnabled, setMultiServiceCreationFieldEnabled] = useState<boolean>(false);
  const [childSummary, setChildSummary] = useState<JSX.Element | string>('');
  const [canMultipleServiceCreate, setCanMultipleServiceCreate] = useState<boolean>(false);
  const [isAdvancedMode, setIsAdvancedMode] = useState<boolean>(false);
  const [serviceTypeMappingData, setServiceTypeMappingData] = useState<CustomNodeAttributeType[]>([]);

  const [locationId, setLocationId] = React.useState<number>();
  const [locationText, setLocationText] = useState<string>();
  const [locationNodeOptions, setLocationNodeOptions] = useState<Array<LocationNodeData>>();
  const [mainLocationNode, setMainLocationNode] = useState<LocationNodeData>();
  const [subLocationNode, setSubLocationNode] = useState<LocationNodeData>();
  const [locationSearchError, setLocationSearchError] = useState(false);
  const [suggestedNodesFromAllocation, setSuggestedNodesFromAllocation] = useState<LocationNodeData[]>([]);
  const [userAllocationPermission, setUserAllocationPermission] = useState(false);
  const [contactData, setContactData] = useState<ContactData[]>([]);
  const [structureContactDataset, setStructureContactDataset] = useState<ContactDataType[]>([]);
  const [editContactData, setEditContactData] = useState<ContactData>();
  const [validate, setValidate] = useState(false);
  const [structureCreationPermission, setStructureCreationPermission] = useState(false);
  const [structureUpdatePermission, setStructureUpdatePermission] = useState(false);
  const [openLocationCreateUpdatePopup, setOpenLocationCreateUpdatePopup] = useState(false);

  const [clientHelperText, setClientHelperText] = useState('');
  const [locationHelperText, setLocationHelperText] = useState('');
  const [reference1HelperText, setReference1HelperText] = useState('');
  const [reference2HelperText, setReference2HelperText] = useState('');
  const [reference3HelperText, setReference3HelperText] = useState('');
  const [serialNoHelperText, setSerialNoHelperText] = useState('');
  const [notesHelperText, setNotesHelperText] = useState('');
  const [selectedRules, setSelectedRules] = useState([]);
  const [clientIdSelected, setClientIdSelected] = useState('');
  const [serialPartType, setSerialPartType] = useState<NodePartTypeData>();

  const { interceptedFormsObject, setInterceptedFormsObject } = useContext<FormInterceptorContextProps>(FormInterceptorContext);
  const [repeatableFormData, setRepeatableFormData] = useState({ templateId: 0, templateTitle: '' });
  const [originalInterceptedForms, setOriginalInterceptedForms] = useState<FormObject[]>([]);
  const [isInteractionSubmiting, setIsInteractionSubmiting] = useState(false);
  const [merchantDamageObjectParcer, setMerchantDamageObjectParcer] = useState<MerchantDamageObjectParcer>();

  const serviceTagsQuery = useGetAllTags(EntityType.TYPE_SERVICES);
  const serviceActionProcessTypesQuery = useGetServiceActionProcessTypes(activeOnly);
  const getStructuresByTypeCategoryCodeAndTypeCode = useGetStructuresByTypeCategoryCodeAndTypeCode(StructureTypeCategory.System, NodeType.Client);
  const getNodeChildrenByNodeType = useGetNodeChildrenByNodeType(selectedParentId, queryNodeType);
  const addServiceQuery = useAddService();
  const addTagsQuery = useAddTags();
  const getNodeAttributesByCustomRange = useGetFilteredNodeAttributesByCustomRange(parentId, nodeAttributeTypes, serviceTypeCode, FaultCategory.Reported, data.actionTypeCode);
  const subLocationsForCreateServicesQuery = useSubLocationsForCreateServices();
  const getServiceTemplatesQuery = useGetServiceTemplates();
  const searchNodeByParentId = useSearchNodeByParentId();
  const getNodeSubTreeQuery = useGetNodeSubTree(locationId?.toString() || '0');
  const getSuggestedNodesFromAllocationEngine = useGetSuggestedNodesFromAllocationEngine(data.serviceTypeCode, data.actionTypeCode, data.processTypeCode, Number(brandId !== '0' ? brandId : parentId,), mainLocationNode?.id);
  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const getCurrentUserVisibility = useGetCurrentUserVisibility();
  const searchParts = useSearchParts();
  const searchAllocationNodes = useSearchAllocationNodes();
  const searchNodeWithParts = useSearchNodeWithParts();
  const getNodeChildrenByNodeTypeAsEnabled = useGetNodeChildrenByNodeTypeAsEnabled();
  const checkMultipleServiceCreationAccess = useCheckMultipleServiceCreationAccess(parentId);
  const childLocationCountGroupedByNodeType = useChildLocationCountGroupedByNodeType(subLocationNode?.id?.toString() || mainLocationNode?.id?.toString() || '');
  const addServiceBulk = useAddServiceBulk();
  const getServiceTypeMappings = useGetNodeAttributesByCustomRange(parentId, [NodeAttributeType.ServiceTypeMappings]);
  const getCurrentUser = useGetCurrentUser();
  const updateProfileConfig = useUpdateProfileConfig(getCurrentUser?.data?.id as number);

  const createStructure = useCreateStructure();
  const modifyStructuresToggleActive = useModifyStructuresToggleActive();
  const updatePartAdditionalData = useUpdatePartAdditionalData(merchantDamageObjectParcer?.serialData?.partId || 0);
  const submitFormsDataWithOriginalPayloadQuery = useSubmitFormsDataWithOriginalPayload(0);

  const focusRef = useRef<any>({});
  const isClientCustomerUser = !!getCurrentUserVisibility.data?.hasClientCustomer;
  const isError: boolean =
    isHaveError(data, selectedParentHierarchy, selectedRules) ||
    locationSearchError ||
    !mainLocationNode?.id ||
    isDropDownEmpty(data.priorityCode);

  TabHandler(`Create Service | ${PLATFORM_NAME}`);

  const isAtAnyLocation = () => {
    return serviceTargetOptions?.some(targetOption => targetOption === ServiceTargetOptions.AnyStructureLocation || targetOption === ServiceTargetOptions.SerialNumberAtAnyLocation);
  };

  useEffect(() => {
    // Remove any intercepted forms data when moving away from the page
    return () => {
      setInterceptedFormsObject(undefined);
      setOriginalInterceptedForms([]);
    };
  }, [location.pathname]);

  useEffect(() => {
    onChangePriority(data.priorityCode);
  }, [data.priorityCode]);

  useEffect(() => {
    getStructuresByTypeCategoryCodeAndTypeCode.refetch();
  }, []);

  useEffect(() => {
    if (locationId && (!selectedParentHierarchy || !isAtAnyLocation())) {
      getNodeSubTreeQuery.refetch();
    }
  }, [mainLocationNode]);

  useEffect(() => {
    if (getUserPermissionsQuery.data) {
      setUserAllocationPermission(isUserHasPermission(ActionPermissions.Service_Edit_Allocation_Primary, getUserPermissionsQuery.data));
      setStructureCreationPermission(isUserHasPermission(ActionPermissions.Structure_Create, getUserPermissionsQuery.data));
      setStructureUpdatePermission(isUserHasPermission(ActionPermissions.Structure_Edit, getUserPermissionsQuery.data));
    }
  }, [getUserPermissionsQuery.data]);

  useEffect(() => {
    if (getNodeSubTreeQuery.data) {
      const overrideClientHiearachy = !isAtAnyLocation();
      const clientNode = getNodeSubTreeQuery.data.find(item => item.nodeTypeCode === NodeType.Client);

      if (clientNode) {
        setSelectedClientId(clientNode.id);

        if(overrideClientHiearachy) {
          setClients([{ value: clientNode.id.toString(), label: clientNode.name, disabled: !clientNode.isActive }]);
        }
      }

      const contractNode = getNodeSubTreeQuery.data.find(item => item.nodeTypeCode === NodeType.Contract);

      if (contractNode) {
        setSelectedContractId(contractNode.id);

        if(overrideClientHiearachy) {
          setContracts(NONE_ITEM.map((item) => ({ ...item, disabled: false })).concat([{ value: contractNode.id.toString(), label: contractNode.name, disabled: !contractNode.isActive }]));
        }
        else {
          setSelectedParentId(clientNode?.id.toString() || '');
          setQueryNodeType(NodeType.Contract);
        }
      }

      const brandNode = getNodeSubTreeQuery.data.find(item => item.nodeTypeCode === NodeType.Brand);
      clientNode && setSelectedParentHierarchy(clientNode ? `${clientNode?.name}/${contractNode?.name || ''}/${brandNode?.name || ''}` : '-');
      if(brandNode){
        setBrandId(brandNode?.id.toString());
        setSelectedBrandId(brandNode.id);
        setBrands(NONE_ITEM.map((item) => ({ ...item, disabled: false })).concat([{ value: brandNode.id.toString(), label: brandNode.name, disabled: !brandNode.isActive }]));
      }

      // For service creation with serial(when sibling has a parent without client hierarchy)
      if (!clientNode && serialPartType) {
        setSelectedClientId(serialPartType.clientId);
        setClients([{ value: serialPartType?.clientId.toString(), label: serialPartType?.clientName, disabled: false }]);
        
        const contractId = serialPartType?.contractIds && serialPartType?.contractIds?.split(',').length === 1 ? serialPartType?.contractIds?.split(',')[0] : '0';
        const contractName = serialPartType?.contractNames && serialPartType?.contractNames?.split(',').length === 1 ? serialPartType?.contractNames?.split(',')[0] : 'none';
        setSelectedContractId(parseInt(contractId));
        const contracts = contractId !== '0' ? NONE_ITEM.map((item) => ({ ...item, disabled: false })).concat([{ value: contractId, label: contractName, disabled: false }]) : NONE_ITEM.map((item) => ({ ...item, disabled: false }));
        setContracts(contracts);

        setSelectedParentId(serialPartType.clientId.toString());
        setParentId(serialPartType.clientId.toString());
        setQueryNodeType(NodeType.Contract);
        setSelectedParentHierarchy(`${serialPartType?.clientName}/${contractName}/none`);
        setSelectedBrandId(0);
      }

      //TODO 
      //Here we are setting client node id as brandId, need to check this logic and refactor or remove
      /*  if (clientNode) {
        setBrandId(locationId?.toString() || '');
      } */
    }
  }, [getNodeSubTreeQuery.data]);

  // For service creation with serial where sibling has no parent
  const updateClientHierarchyForSiblingWithoutParent = (partTypeData: NodePartTypeData) => {
    setSelectedClientId(partTypeData.clientId);
    setClients([{ value: partTypeData?.clientId.toString(), label: partTypeData?.clientName, disabled: false }]);
    
    const contractId = partTypeData?.contractIds && partTypeData?.contractIds?.split(',').length === 1 ? partTypeData?.contractIds?.split(',')[0] : '0';
    const contractName = partTypeData?.contractNames && partTypeData?.contractNames?.split(',').length === 1 ? partTypeData?.contractNames?.split(',')[0] : 'none';
    setSelectedContractId(parseInt(contractId));
    const contracts = contractId !== '0' ? NONE_ITEM.map((item) => ({ ...item, disabled: false })).concat([{ value: contractId, label: contractName, disabled: false }]) : NONE_ITEM.map((item) => ({ ...item, disabled: false }));
    setContracts(contracts);

    setSelectedParentId(partTypeData.clientId.toString());
    setParentId(partTypeData.clientId.toString());
    setQueryNodeType(NodeType.Contract);
    setSelectedParentHierarchy(`${partTypeData?.clientName}/${contractName}/none`);
  };

  useEffect(() => {
    if (brandId !== '0' || parentId) {
      getSuggestedNodesFromAllocationEngine.refetch();
    }
  }, [brandId,mainLocationNode,subLocationNode]);


  useEffect(() => {
    serviceActionProcessTypesQuery.data && setServiceCommonTypes(serviceActionProcessTypesQuery.data);
  }, [serviceActionProcessTypesQuery.data]);

  useEffect(() => {
    if (getSuggestedNodesFromAllocationEngine.data && userAllocationPermission) {
      const data = getSuggestedNodesFromAllocationEngine.data;
      data.forEach(item => item.isSuggestedNode = true);
      setSuggestedNodesFromAllocation(data);
    }
  }, [getSuggestedNodesFromAllocationEngine.data]);

  useEffect(() => {
    serviceTagsQuery.data && setAllTags(serviceTagsQuery.data);
  }, [serviceTagsQuery.data]);

  useEffect(() => {
    getStructuresByTypeCategoryCodeAndTypeCode.data && setClients(
      getStructuresByTypeCategoryCodeAndTypeCode.data.map(item => ({ value: item.id.toString(), label: item.name, disabled: !item.isActive }))
    );
  }, [getStructuresByTypeCategoryCodeAndTypeCode.data]);

  useEffect(() => {
    if (getNodeChildrenByNodeType.data) {
      if (queryNodeType === NodeType.Contract) {
        const contractMenuList = getNodeChildrenByNodeType.data.map(item => ({
          value: item.id.toString(),
          label: item.name,
          disabled: !item.isActive
        }));
        const contractMenuListWithAny = NONE_ITEM.map((item) => ({ ...item, disabled: false })).concat(contractMenuList);
        setContracts(contractMenuListWithAny);

        if(selectedContractId > 0) {
          setSelectedParentId(selectedContractId.toString());
          setParentId(selectedContractId.toString());
          setQueryNodeType(NodeType.Brand);
        }
        serialPartType && selectedContractId === 0 && setSerialPartType(undefined);
      }
      else if (queryNodeType === NodeType.Brand) {
        const brandMenuList = getNodeChildrenByNodeType.data.map(item => ({
          value: item.id.toString(),
          label: item.name,
          disabled: !item.isActive
        }));
        const brandMenuListWithAny = NONE_ITEM.map((item) => ({ ...item, disabled: false })).concat(brandMenuList);
        setBrands(brandMenuListWithAny);
        serialPartType && setSerialPartType(undefined);
      }
    }
  }, [getNodeChildrenByNodeType.data]);


  useEffect(() => {
    if (selectedParentId && queryNodeType) {
      getNodeChildrenByNodeType.refetch();
    }
  }, [selectedParentId, queryNodeType]);

  useEffect(() => {
    if (parentId && nodeAttributeTypes && serviceTypeCode && data.actionTypeCode) {
      getNodeAttributesByCustomRange.refetch();
    }
  }, [parentId, nodeAttributeTypes, serviceTypeCode, data.actionTypeCode]);

  useEffect(() => {
    if (getNodeAttributesByCustomRange.data) {
      setNodeAttributes(getNodeAttributesByCustomRange.data);
      setData(prev => ({ ...prev, priorityCode: getInitialPriority(getNodeAttributesByCustomRange.data) }));
    }
  }, [getNodeAttributesByCustomRange.data]);

  useEffect(() => {
    if (parentId) {
      getServiceTypeMappings.refetch();
    }
  }, [parentId]);

  useEffect(() => {
    if (getServiceTypeMappings.data) {
      setServiceTypeMappingData(getServiceTypeMappings.data);
    }
  }, [getServiceTypeMappings.data]);

  useEffect(() => {
    if (getCurrentUser.data?.config?.serviceAdvancedMode && !isClientCustomerUser) {
      setIsAdvancedMode(getCurrentUser.data?.config?.serviceAdvancedMode);
    } else {
      setIsAdvancedMode(false);
    }
  }, [getCurrentUser.data, isClientCustomerUser]);

  useEffect(() => {
    if (parentId) {
      checkMultipleServiceCreationAccess.refetch();
    }
  }, [parentId]);

  useEffect(() => {
    if (checkMultipleServiceCreationAccess.data) {
      setData(prev => ({ ...prev, isMultipleCreationEnabled: true }));
    } else {
      setData(prev => ({ ...prev, isMultipleCreationEnabled: false }));
      setMultiServiceCreationFieldEnabled(false);
    }
  }, [checkMultipleServiceCreationAccess.data]);

  useEffect(() => {
    if (mainLocationNode?.id || subLocationNode?.id || multiServiceCreationFieldEnabled) {
      childLocationCountGroupedByNodeType.refetch();
    }
  }, [mainLocationNode?.id, subLocationNode?.id, multiServiceCreationFieldEnabled]);

  useEffect(() => {
    if (childLocationCountGroupedByNodeType.data) {
      const data = childLocationCountGroupedByNodeType.data;

      if (!data || Object.keys(data).length === 0) {
        setChildSummary('No Children found, check Service Location.');
        setCanMultipleServiceCreate(false);
        return;
      }

      setCanMultipleServiceCreate(true);

      // Separate "Total"
      const { Total, ...rest } = data;

      // Build JSX
      const jsx = (
        <>
          Children Found:&nbsp;
          {Object.entries(rest).map(([key, value], idx) => (
            <span key={key}>
              {key} x {value as number}
              {idx < Object.entries(rest).length - 1 && ', '}
            </span>
          ))}

          {Object.keys(rest).length > 0 && ', '}
          <strong>Total: {Total}</strong>
        </>
      );

      setChildSummary(jsx);
    }
  }, [childLocationCountGroupedByNodeType.data]);

  useEffect(() => {
    if (getServiceTemplatesQuery.data) {
      let count = 0;
      const templates: ServiceTemplateDataType[] = [];
      getServiceTemplatesQuery.data.map(item => {
        item.nodeAssociations.map(data => {
          const obj = {
            id: ++count,
            name: item.name,
            description: data.name,
            data: item.data,
            icon: item.data.icon
          };
          templates.push(obj);
        });
      });
      setTemplateData(templates);
    }
  }, [getServiceTemplatesQuery.isLoading, getServiceTemplatesQuery.isSuccess]);

  useEffect(() => {
    if (serviceTypeCode) {
      const selectedServiceType = serviceCommonTypes.serviceTypes.find((serviceType) => serviceType.code === serviceTypeCode);
      let defaultTargetOption: ServiceTargetOptions | null = null;

      if (selectedServiceType?.targetOptions?.length === 1) {
        defaultTargetOption = selectedServiceType?.targetOptions[0].serviceTarget.code;

      }
      else if (selectedServiceType?.targetOptions) {
        const defaultOption = selectedServiceType?.targetOptions?.find(targetOption => targetOption.isDefault);
        if (defaultOption) {
          defaultTargetOption = defaultOption.serviceTarget.code;
        }
        else {
          defaultTargetOption = selectedServiceType?.targetOptions[0].serviceTarget.code;
        }
      }

      setDefaultTargetOption(defaultTargetOption);
      setServiceTargetOptions(selectedServiceType?.serviceTargetOptions || null);
    }
  }, [serviceTypeCode]);

  useEffect(() => {
    if (selectedRules?.length > 0 && data.priorityCode) {
      if (isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference1) && isEmptyString(data.referenceNo1)) {
        setReference1HelperText(`${ServiceCreationRuleTypes.Reference1} ${EMPTY_FIELD_MESSAGE}`);
      }
      if (isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference2) && isEmptyString(data.referenceNo2)) {
        setReference2HelperText(`${ServiceCreationRuleTypes.Reference2} ${EMPTY_FIELD_MESSAGE}`);
      }
      if (isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference3) && isEmptyString(data.referenceNo3)) {
        setReference3HelperText(`${ServiceCreationRuleTypes.Reference3} ${EMPTY_FIELD_MESSAGE}`);
      }
      if (isRuleExist(selectedRules, ServiceCreationRuleTypes.SerialNumber) && (isEmptyString(data.reportedSerialNumber) || !data.reportedSerialNumber)) {
        setSerialNoHelperText(`${ServiceCreationRuleTypes.SerialNumber} ${EMPTY_FIELD_MESSAGE}`);
      }
      if (isRuleExist(selectedRules, ServiceCreationRuleTypes.Notes) && isEmptyString(data.notes)) {
        setNotesHelperText(`${ServiceCreationRuleTypes.Notes} ${EMPTY_FIELD_MESSAGE}`);
      }
    }
  }, [selectedRules]);

  useEffect(() => {
    if (isRuleExist(selectedRules, ServiceCreationRuleTypes.Notes)) {
      let value: string = data.notes;
      if (value === RICH_TEXT_EDITOR_EMPTY_TEXT) {
        value = '';
      }
      setNotesHelperText(emptyFieldValidation(value, ServiceCreationRuleTypes.Notes));
    }
  }, [data.notes]);

  useEffect(() => {
    selectedClientId && setClientIdSelected(selectedClientId.toString());
  }, [selectedClientId]);

  useEffect(() => {
    if (interceptedFormsObject?.forms && !originalInterceptedForms.length) {
      const originalForms = [...interceptedFormsObject.forms];
      setOriginalInterceptedForms(originalForms);
    }
  }, [interceptedFormsObject?.forms]);

  useEffect(() => {
    if (interceptedFormsObject?.forms && repeatableFormData.templateId !== 0 && repeatableFormData.templateTitle !== '') {
      const newForm = originalInterceptedForms.find((form) => form.templateId === repeatableFormData.templateId && form.title === repeatableFormData.templateTitle);
      const updatedForms = [...interceptedFormsObject.forms];
      newForm && updatedForms.push({ ...newForm, id: Math.max(...updatedForms.map((form) => form.id)) + 1 });
      setInterceptedFormsObject({ ...interceptedFormsObject, forms: updatedForms });
    }
    (repeatableFormData.templateId !== 0 || repeatableFormData.templateTitle !== '') && setRepeatableFormData({ templateId: 0, templateTitle: '' });
  }, [repeatableFormData]);

  const onChangeSelectedTags = (value: (string | AutocompleteTagType)[]) => {
    setSelectedTags(value);
  };

  const onChangeNewTags = (value: Tag[]) => {
    setNewTags(value);
  };

  const onChangeNodeType = (nodeType: NodeType, parentId: string) => {
    const nodeAttributeTypes = [NodeAttributeType.PriorityAndServices, NodeAttributeType.Models, NodeAttributeType.FaultCategory, NodeAttributeType.FaultCode];
    if (!serialPartType) {
      setSelectedParentId(parentId);
      setQueryNodeType(nodeType);
      setParentId(parentId);
    }
    setNodeAttributeTypes(nodeAttributeTypes);
  };

  const onChangePriority = (value: any) => {
    const selectedPriority: any = nodeAttributes.filter((data: any) => { return data?.id == value; });

    if (selectedPriority.length && selectedPriority[0].value.selectedServiceCreationTypeValue) {
      setSelectedRules(selectedPriority[0].value.selectedServiceCreationTypeValue);
    } else setSelectedRules([]);

    setReference1HelperText('');
    setReference2HelperText('');
    setReference3HelperText('');
    setSerialNoHelperText('');
    setNotesHelperText('');
  };

  const getServiceObject = () => {
    return {
      id: 0,
      serviceStatus: ServiceStatusLabel.New,
      serviceStatusCode: ServiceStatusCode.New,
      serviceTypeCode: data.serviceTypeCode,
      actionTypeCode: data.actionTypeCode,
      processTypeCode: data.processTypeCode,
      clientId: selectedClientId,
      contractId: selectedContractId,
      brandId: selectedBrandId,
      clientHierarchy:`[${selectedClientId},${selectedContractId},${selectedBrandId}]`,
      clientHierarchyId: brandId !== '0' ? brandId : parentId,
      priorityCode: data.priorityCode,
      subLocationNodeId: subLocationNode?.id,
      mainLocationNodeId: mainLocationNode?.id,
      scheduledAt: formatDate(data.scheduledAt, ''),
      notes: data.notes,
      serviceAttributes: getServiceAttributes(data),
      userDepotAllocationNodeId: data.userDepotAllocationNodeId,
      elementForDisplaying: structureContactDataset
    } as any;
  };

  const handleClose = (event: SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const handleServiceCreate = async () => {
    if (isClientEmpty(selectedParentHierarchy)) {
      setClientHelperText(`Client ${EMPTY_FIELD_MESSAGE}`);
    }
    if (!mainLocationNode?.id) {
      setLocationHelperText(`Location ${EMPTY_FIELD_MESSAGE}`);
    }
    if (isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference1) && isEmptyString(data.referenceNo1)) {
      setReference1HelperText(`${ServiceCreationRuleTypes.Reference1} ${EMPTY_FIELD_MESSAGE}`);
    }
    if (isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference2) && isEmptyString(data.referenceNo2)) {
      setReference2HelperText(`${ServiceCreationRuleTypes.Reference2} ${EMPTY_FIELD_MESSAGE}`);
    }
    if (isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference3) && isEmptyString(data.referenceNo3)) {
      setReference3HelperText(`${ServiceCreationRuleTypes.Reference3} ${EMPTY_FIELD_MESSAGE}`);
    }
    if (isRuleExist(selectedRules, ServiceCreationRuleTypes.SerialNumber) && isEmptyString(data.reportedSerialNumber)) {
      setSerialNoHelperText(`${ServiceCreationRuleTypes.SerialNumber} ${EMPTY_FIELD_MESSAGE}`);
    }
    if (isRuleExist(selectedRules, ServiceCreationRuleTypes.Notes) && isEmptyString(data.notes)) {
      setNotesHelperText(`${ServiceCreationRuleTypes.Notes} ${EMPTY_FIELD_MESSAGE}`);
    }

    if (isError) {
      setValidate(true);
      return;
    }

    const service = getServiceObject();

    const timeout = (delay: number) => {
      return new Promise((res) => setTimeout(res, delay));
    };

    let existingTags = getSelectedExistingTagsMapped(selectedTags);
    let freshTags = getNewTagsMapped(newTags);

    // Validating and submitting interaction forms if available
    if (interceptedFormsObject?.forms && interceptedFormsObject?.forms?.length > 0) {
      const { hasErrorFields, hasByPassedFields, blockInteractionBypassed, updatedForms } = validateInteractionForms(interceptedFormsObject, service, getUserPermissionsQuery.data || []);
      if (updatedForms?.length > 0 && (hasErrorFields || hasByPassedFields || blockInteractionBypassed)) {
        setInterceptedFormsObject({ ...interceptedFormsObject, forms: updatedForms });
      };
      if (hasErrorFields) {
        return;
      } else {
        // Submit interactions
        setIsInteractionSubmiting(true);
        const updatedFormsObject = { ...interceptedFormsObject, forms: updatedForms };
        const { interactionSuccess, updatedInterceptedFormsObject, serviceResult, interactionExistingTags, interactionFreshTags } = await submitAddServicePageInteractionForms(updatedFormsObject, service, merchantDamageObjectParcer);
        if (interactionSuccess) {
          setOriginalInterceptedForms([]);
          setInterceptedFormsObject(undefined);
          if (multiServiceCreationFieldEnabled && canMultipleServiceCreate) {
            setOpenSnackbar(true);
            await timeout(1000);
            navigate('/services');
          } else {
            if (existingTags?.length > 0 || freshTags?.length > 0) {
              existingTags = existingTags.concat(interactionExistingTags);
              freshTags = freshTags.concat(interactionFreshTags);

              await addTagsQuery.mutateAsync({
                entityTypeId: EntityType.TYPE_SERVICES,
                entityId: serviceResult.id,
                freshTags,
                existingTags
              });
            }
            setOpenSnackbar(true);
            await timeout(1000);
            navigate(`/services/${serviceResult.id}`);
          }
        } else {
          updatedInterceptedFormsObject && setInterceptedFormsObject(updatedInterceptedFormsObject);
        }
        setIsInteractionSubmiting(false);
        return;
      }
    }

    if (multiServiceCreationFieldEnabled && canMultipleServiceCreate) {
      const tag: any = {
        entityTypeId: EntityType.TYPE_SERVICES,
        entityId: service.id,
        freshTags,
        existingTags
      };

      const bulkServiceCreateResult: any = await addServiceBulk.mutateAsync({ service, tag });
      if (!bulkServiceCreateResult?.interactionFormData?.length) {
        setOpenSnackbar(true);
        await timeout(1000);
        navigate('/services');
      }
    } else {
      const serviceResult: any = await addServiceQuery.mutateAsync(service);
      if (!serviceResult?.interactionFormData?.length) {
        await addTagsQuery.mutateAsync({
          entityTypeId: EntityType.TYPE_SERVICES,
          entityId: serviceResult.id,
          freshTags,
          existingTags
        }).then(async () => {
          setOpenSnackbar(true);
          await timeout(1000);
          navigate(`/services/${serviceResult.id}`);
        });

        setOpenSnackbar(true);
      }
    }
  };

  const saveServiceModeConfig = async (event: any) => {
    if (isAdvancedMode && data.serviceTypeCode && data.actionTypeCode && data.processTypeCode) {
      // Reverse mapping of service type, action type, process type to service type mapping when mode is switched to Simple
      const serviceTypeMapping = serviceTypeMappingData.find((mapping) => mapping.value?.selectedServiceType === data.serviceTypeCode && mapping.value?.selectedActionType === data.actionTypeCode && mapping.value?.selectedProcessType === data.processTypeCode);
      setData(prev => ({ ...prev, serviceTypeMapping: serviceTypeMapping ? serviceTypeMapping?.id.toString() : '' }));
    }
    setIsAdvancedMode(event.target.checked);
    // Save service creation mode config in the user config
    const updatedConfig = {
      ...getCurrentUser.data?.config,
      serviceAdvancedMode: event.target.checked
    };
    await updateProfileConfig.mutateAsync({
      config: updatedConfig
    });
    getCurrentUser.refetch();
  };
  
  const onSetCreatedLocation = async (createdLocationId: number) => {
    const subLocationsResult = createdLocationId && createdLocationId !== 0 && (await subLocationsForCreateServicesQuery.mutateAsync(createdLocationId));
    createdLocationId !== 0 && setLocationId(createdLocationId);

    if (subLocationsResult && subLocationsResult.length > 0) {
      const main = subLocationsResult.find(subLocation => subLocation.isMain);
      const sub = subLocationsResult.find(subLocation => !subLocation.isMain && subLocation.isSelected);

      //if we have a main node and a selected subLocation, select them by default
      if (main && sub) {
        setMainLocationNode(main);
        setSubLocationNode(sub);
        setLocationText(`${main.name || ''} ${(main.name && sub.name) ? '/' : ''} ${sub.name || ''}`.trim());
      }
      else {
        const subLocationsOnly = subLocationsResult.filter(subLocation => !subLocation.isMain);
        if (main?.isSelected && subLocationsOnly.length === 0) {
          setMainLocationNode(main);
          setSubLocationNode(undefined);
          setLocationText(main.name);
        }
        else {
          setLocationNodeOptions(subLocationsResult);
        }
      }

      setLocationSearchError(false);
    } else {
      setLocationNodeOptions(undefined);
      setMainLocationNode(undefined);
      setSubLocationNode(undefined);
      setLocationText(' ');
      setLocationSearchError(true);
    }
  };

  const submitAddServicePageInteractionForms = async (
    interceptedFormsObject: InterceptedFormsObject,
    service: any,
    merchantDamageObjectParcer: MerchantDamageObjectParcer | undefined
  ) => {
    let photoKeys: any;
    if (merchantDamageObjectParcer?.serialData?.partId) {
      photoKeys = await uploadFiles(merchantDamageObjectParcer?.serialData?.partId, merchantDamageObjectParcer?.photos, Folders.PartImages);
    }
  
    let interactionExistingTags: Tag[] = [];
    let interactionFreshTags: { text: string; colour: string }[] = [];
  
    // Sub form activities
    const waitForSubFormActivities = new Promise<boolean>((resolve) => {
      (async () => {
        for (const form of interceptedFormsObject.forms) {
          for (const container of form.containers) {
            if (container.isDecisionPrompt && (container.questionData?.hasValue && !container.questionData?.isYes || !container.questionData?.hasValue)) {
              continue;
            }
            for (const field of container.fields) {
              if (field.type === FormFieldType.ServiceCreation || field.type === FormFieldType.ServiceSearch) {
                field.value = undefined;
              }
              
              // Create sub location or deactivate nodes if a Modify Structure Form exists
              if (field.type === FormFieldType.ModifyStructure) {
                if (field.preSelectedData?.structureType === ModifyStructureFormModifyStructureType.CreateLocation && !service.subLocationNodeId && !hasFormFieldValueError(field, service)) {
                  if (!field.value?.locationName || isEmptyString(field.value?.locationName)) {
                    const updatedForms = updateErrorState(interceptedFormsObject, form, container, field);
                    if (updatedForms && updatedForms?.length > 0) {
                      interceptedFormsObject.forms = updatedForms;
                    }
                    return resolve(false);
                  } else {
                    const newSublocation = await createStructure.mutateAsync({
                      parentNodeId: service.mainLocationNodeId,
                      name: field.value?.locationName,
                      code: field.value?.locationName,
                      nodeTypeCode: field.value.modifyStructure.selectedValue,
                      isActive: true,
                      attributes: []
                    });
  
                    if (newSublocation) {
                      service.subLocationNodeId = newSublocation.id;
                    }
                  }
                } else if (field.preSelectedData?.structureType === ModifyStructureFormModifyStructureType.ToggleActive) {
                  await modifyStructuresToggleActive.mutateAsync({
                    serviceMainLocationNodeId: service.mainLocationNodeId,
                    serviceSubLocationNodeId: service.subLocationNodeId,
                    toggleActiveType: field.preSelectedData?.selectedValue
                  });
                }
              }
  
              // Update parameters if a Parameter Via UI Form exist
              if (field.type === FormFieldType.SetParameter && !hasFormFieldValueError(field, service)) {
                // Status
                if (field.preSelectedData?.parameterType === ParameterDataFormParameterType.Status) {
                  if (!field.value || isEmptyString(field.value)) {
                    const updatedForms = updateErrorState(interceptedFormsObject, form, container, field);
                    if (updatedForms && updatedForms?.length > 0) {
                      interceptedFormsObject.forms = updatedForms;
                    }
                    return resolve(false);
                  } else {
                    service.serviceStatusCode = field.value;
                  }
                }
  
                // PrimaryAllocation
                if (field.preSelectedData?.parameterType === ParameterDataFormParameterType.PrimaryAllocation) {
                  if (!field.value?.id) {
                    const updatedForms = updateErrorState(interceptedFormsObject, form, container, field);
                    if (updatedForms && updatedForms?.length > 0) {
                      interceptedFormsObject.forms = updatedForms;
                    }
                    return resolve(false);
                  } else {
                    service.userDepotAllocationNodeId = field.value?.id;
                  }
                }
  
                // SecondaryAllocation
                if (field.preSelectedData?.parameterType === ParameterDataFormParameterType.SecondaryAllocation) {
                  if (!field.value?.id) {
                    const updatedForms = updateErrorState(interceptedFormsObject, form, container, field);
                    if (updatedForms && updatedForms?.length > 0) {
                      interceptedFormsObject.forms = updatedForms;
                    }
                    return resolve(false);
                  } else {
                    service.userDepotSecondaryAllocationNodeId = field.value?.id;
                  }
                }
  
                // UpdateSubLocation
                if (field.preSelectedData?.parameterType === ParameterDataFormParameterType.UpdateSubLocation) {
                  if (!field.value?.id) {
                    const updatedForms = updateErrorState(interceptedFormsObject, form, container, field);
                    if (updatedForms && updatedForms?.length > 0) {
                      interceptedFormsObject.forms = updatedForms;
                    }
                    return resolve(false);
                  } else {
                    service.subLocationNodeId = field.value?.id;
                  }
                }
  
                // ScheduledDate
                if (field.preSelectedData?.parameterType === ParameterDataFormParameterType.ScheduledDate) {
                  if (!field.value) {
                    const updatedForms = updateErrorState(interceptedFormsObject, form, container, field);
                    if (updatedForms && updatedForms?.length > 0) {
                      interceptedFormsObject.forms = updatedForms;
                    }
                    return resolve(false);
                  } else {
                    service.scheduledAt = formatDate(field.value);
                  }
                }
  
                // ReportedSerial
                if (field.preSelectedData?.parameterType === ParameterDataFormParameterType.ReportedSerial) {
                  if (!field.value?.name) {
                    const updatedForms = updateErrorState(interceptedFormsObject, form, container, field);
                    if (updatedForms && updatedForms?.length > 0) {
                      interceptedFormsObject.forms = updatedForms;
                    }
                    return resolve(false);
                  } else {
                    service.reportedSerialNumber = field.value?.id;
                  }
                }
  
                // SetTags
                if (field.preSelectedData?.parameterType === ParameterDataFormParameterType.SetTag) {
                  if ((field.value.selectedTags && field.value.selectedTags.length < 1) && (field.value.newTags && field.value.newTags.length < 1)) {
                    const updatedForms = updateErrorState(interceptedFormsObject, form, container, field);
                    if (updatedForms && updatedForms?.length > 0) {
                      interceptedFormsObject.forms = updatedForms;
                    }
                    return resolve(false);
                  } else {
                    interactionExistingTags = getSelectedExistingTagsMapped(field.value.selectedTags);
                    interactionFreshTags = getNewTagsMapped(field.value.newTags);
                  }
                }
              }

              if (photoKeys && container.id === merchantDamageObjectParcer?.containerId && field.id === merchantDamageObjectParcer.fieldId) {
                field.value.photoKeys = Object.values(photoKeys);
                field.value = JSON.stringify(field.value);
              }
            }
          }
        }
          
        // Wait for 1000 milliseconds until the backend gets ready for the interactions
        setTimeout(() => {
          return resolve(true);
        }, 1000);
      })();
    });
  
    const success = await waitForSubFormActivities;
    let interactionSuccess = false;
    let serviceResult: any;
    if (success) {
      if (interceptedFormsObject.payload?.service) { // Service bulk creation payload
        interceptedFormsObject.payload = {
          ...interceptedFormsObject.payload,
          ...interceptedFormsObject.payload?.service && { service: service }
        };
        if (interceptedFormsObject.payload.tag?.existingTags?.length > 0 || interceptedFormsObject.payload.tag?.freshTags?.length > 0) {
          const updatedExistingTags = (interceptedFormsObject.payload.tag?.existingTags || []).concat(interactionExistingTags);
          const updatedFreshTags = (interceptedFormsObject.payload.tag?.freshTags || []).concat(interactionFreshTags);
          interceptedFormsObject.payload.tag = {
            entityTypeId: EntityType.TYPE_SERVICES,
            entityId: service.id,
            freshTags: updatedFreshTags,
            existingTags: updatedExistingTags
          };
        }
      } else {
        interceptedFormsObject.payload = {
          ...interceptedFormsObject.payload,
          ...service
        };
      }
      serviceResult = await submitFormsDataWithOriginalPayloadQuery.mutateAsync(interceptedFormsObject);
      interactionSuccess = true;
    }
  
    if (merchantDamageObjectParcer?.serialData?.partId && serviceResult?.id) {
      await uploadFiles(serviceResult.id, merchantDamageObjectParcer?.photos, Folders.ServiceAttachments, ServiceAttachmentUploadFeature.InteractionPhotoUpload);
      await updatePartAdditionalData.mutateAsync({
        serviceId: serviceResult.id,
        photoKeys: Object.values(photoKeys).toString()
      });
    }
    return { interactionSuccess, updatedInterceptedFormsObject: interceptedFormsObject, serviceResult, interactionExistingTags, interactionFreshTags };
  };

  const notesComponent = <Grid size={{ xs: 12 }}>
    <Box>
      <FormControl
        fullWidth
        required={isRuleExist(selectedRules, ServiceCreationRuleTypes.Notes)}
        error={!!notesHelperText}>
        <RichTextEditor
          value={data?.notes || ''}
          onValueChange={(value: string) => {
            setData(prev => ({ ...prev, notes: value }));
          }}
          error={!!notesHelperText}
        />
        {notesHelperText && (
          <FormHelperText>{notesHelperText}</FormHelperText>
        )}
      </FormControl>
    </Box>
  </Grid>;

  if (!getCurrentUserVisibility.data) {
    return getCurrentUserVisibility.isLoading && <Grid ml="570px"><Loader color={LoaderColorProps.Success} /></Grid>;
  }

  return (
    <PageContainer>
      <Grid container direction="row" alignItems="center">
        <ServiceTypography variant={TypographyVariantProps.H5} fontWeight={600}>
          Create Service
        </ServiceTypography>
        {!isClientCustomerUser && (
          <ModeSwitchGrid>
            <FormControlLabel
              control={
                <Switch
                  color={ColorProps.Primary}
                  onChange={(event) => saveServiceModeConfig(event)}
                  checked={isAdvancedMode}
                />
              }
              label={'Advanced Mode'}
              labelPlacement={FormControlLabelPlacementProps.End}
            />
          </ModeSwitchGrid>
        )}
      </Grid>
      <Grid container>
        {isAdvancedMode && (
          <Grid>
            <ServiceTemplateList
              templateData={templateData}
              expanded={true}
              onClickItem={async (obj) => {
                setData(SAMPLE_SERVICE);
                setSelectedParentHierarchy('');
                setSelectedClientId(NaN);
                setSelectedContractId(NaN);
                setSelectedBrandId(NaN);
                setLocationNodeOptions(undefined);
                setMainLocationNode(undefined);
                setSubLocationNode(undefined);
                setLocationText('');
                setLocationSearchError(true);
                setLocationId(NaN);
                setBrandId('0');
                setParentId('');
                setSelectedParentId('');
                setSerialPartType(undefined);
              
                if (obj?.data?.autoFill?.serviceTypeCode) {
                  setServiceTypeCode(obj.data.autoFill.serviceTypeCode);
                }
                if (obj?.data?.autoFill?.priorityCode) {
                  setValidate(false);
                }
                if (obj?.data?.autoFill?.notes) {
                  setNotesHelperText('');
                }

                setData({
                  ...data,
                  serviceTypeCode: obj.data.autoFill.serviceTypeCode || '',
                  actionTypeCode: obj.data.autoFill.actionTypeCode || '',
                  processTypeCode: obj.data.autoFill.processTypeCode || '',
                  priorityCode: obj.data.autoFill.priorityCode || '',
                  notes: obj.data.autoFill.notes || ''
                });

                if (obj?.data?.autoFill?.locationSearch) {
                  setSelectedParentHierarchy(obj.data.autoFill.parentHierarchy);
                  setSelectedClientId(obj.data.autoFill.clientId);
                  setSelectedContractId(obj.data.autoFill.contractId);
                  setSelectedBrandId(obj.data.autoFill.brandId);

                  const contractList = await getNodeChildrenByNodeTypeAsEnabled.mutateAsync({
                    nodeId: obj.data.autoFill.clientId,
                    nodeTypeCode: 'contract'
                  });
                  const contractMenuList = contractList.map(item => ({
                    value: item.id.toString(),
                    label: item.name,
                    disabled: !item.isActive
                  }));
                  const contractMenuListWithAny = NONE_ITEM.map((item) => ({ ...item, disabled: false })).concat(contractMenuList);
                  setContracts(contractMenuListWithAny);

                  const brandList = await getNodeChildrenByNodeTypeAsEnabled.mutateAsync({
                    nodeId: obj.data.autoFill.contractId,
                    nodeTypeCode: 'brand'
                  });
                  const brandMenuList = brandList.map(item => ({
                    value: item.id.toString(),
                    label: item.name,
                    disabled: !item.isActive
                  }));
                  const brandMenuListWithAny = NONE_ITEM.map((item) => ({ ...item, disabled: false })).concat(brandMenuList);
                  setBrands(brandMenuListWithAny);

                  setParentId(obj.data.autoFill.contractId !== 0 ? obj.data.autoFill.contractId.toString() : obj.data.autoFill.clientId.toString());
                  !!obj.data.autoFill.brandId && setBrandId(obj.data.autoFill.brandId.toString());

                  const subLocationsResult = await subLocationsForCreateServicesQuery.mutateAsync(parseInt(obj?.data?.autoFill?.locationSearch));
                  setLocationId(parseInt(obj?.data?.autoFill?.locationSearch));

                  if (subLocationsResult && subLocationsResult.length > 0) {
                    const main = subLocationsResult.find(subLocation => subLocation.isMain);
                    const sub = subLocationsResult.find(subLocation => !subLocation.isMain && subLocation.isSelected);

                    //if we have a main node and a selected subLocation, select them by default
                    if (main && sub) {
                      setMainLocationNode(main);
                      setSubLocationNode(sub);
                      setLocationText(`${main.name || ''} ${(main.name && sub.name) ? '/' : ''} ${sub.name || ''}`.trim());
                    }
                    else {
                      const subLocationsOnly = subLocationsResult.filter(subLocation => !subLocation.isMain);
                      if (main?.isSelected && subLocationsOnly.length === 0) {
                        setMainLocationNode(main);
                        setSubLocationNode(undefined);
                        setLocationText(main.name);
                      }
                      else {
                        setLocationNodeOptions(subLocationsResult);
                      }
                    }

                    setLocationSearchError(false);
                  }
                }

                if (obj?.data?.autoFill?.selectedParentHierarchy) {
                  setSelectedParentHierarchy(obj.data.autoFill.selectedParentHierarchy);
                  setSelectedClientId(obj.data.autoFill.clientId);
                  setSelectedContractId(obj.data.autoFill.contractId);
                  setSelectedBrandId(obj.data.autoFill.brandId);

                  const contractList = await getNodeChildrenByNodeTypeAsEnabled.mutateAsync({
                    nodeId: obj.data.autoFill.clientId,
                    nodeTypeCode: 'contract'
                  });
                  const contractMenuList = contractList.map(item => ({
                    value: item.id.toString(),
                    label: item.name,
                    disabled: !item.isActive
                  }));
                  const contractMenuListWithAny = NONE_ITEM.map((item) => ({ ...item, disabled: false })).concat(contractMenuList);
                  setContracts(contractMenuListWithAny);

                  const brandList = await getNodeChildrenByNodeTypeAsEnabled.mutateAsync({
                    nodeId: obj.data.autoFill.contractId,
                    nodeTypeCode: 'brand'
                  });
                  const brandMenuList = brandList.map(item => ({
                    value: item.id.toString(),
                    label: item.name,
                    disabled: !item.isActive
                  }));
                  const brandMenuListWithAny = NONE_ITEM.map((item) => ({ ...item, disabled: false })).concat(brandMenuList);
                  setBrands(brandMenuListWithAny);

                  setParentId(obj.data.autoFill.contractId !== 0 ? obj.data.autoFill.contractId.toString() : obj.data.autoFill.clientId.toString());
                  !!obj.data.autoFill.brandId && setBrandId(obj.data.autoFill.brandId.toString());

                }

                if (obj?.data?.focus === 'clientSelection') {
                  getStructuresByTypeCategoryCodeAndTypeCode.refetch();
                }

                setFocusField(obj?.data?.focus as string);
                focusRef.current[obj?.data?.focus].focus();
              }}
            />
          </Grid>
        )}
        <Grid size="grow">
          <ServiceBox>
            <Grid container spacing={GRID_SPACING}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Grid container spacing={GRID_SPACING}>
                  <Grid size={{ xs: 10 }}>
                    {locationNodeOptions ?
                      <LocationSearchSelect
                        title="Location Search"
                        options={locationNodeOptions}
                        value={subLocationNode}
                        onClose={() => {
                          setLocationNodeOptions(undefined);
                        }}
                        onChange={(selectedNode) => {
                          const main = locationNodeOptions.find(item => item.isMain);
                          setMainLocationNode(main);
                          setLocationHelperText('');

                          const sub = locationNodeOptions.find(item => !item.isMain && item.id === selectedNode.id);
                          setSubLocationNode(sub);

                          setLocationText(`${main?.name || ''} ${(main?.name && sub?.name) ? '/' : ''} ${sub?.name || ''}`.trim());
                          setLocationNodeOptions(undefined);
                        }}
                      />
                      :
                      <LiveSearchBox
                        title="Location Search"
                        isRequired={true}
                        timeOffset={400}
                        freeSolo={false}
                        multiline={false}
                        inputRef={(element: HTMLElement) => { focusRef.current['locationSearch' as keyof typeof focusRef] = element; }}
                        value={locationText || ''}
                        onClearValue={() => {
                          setLocationText(undefined);
                          setMainLocationNode(undefined);
                          setSubLocationNode(undefined);
                          setLocationNodeOptions(undefined);
                          setSerialPartType(undefined);
                          (data?.reportedSerialNumber || data?.reportedSerialNumberPartId) && setData(prev => ({
                            ...prev,
                            reportedSerialNumberPartId: NaN,
                            reportedSerialNumber: ''
                          }));
                        }}
                        renderItem={(props: any, option: any) => {
                          const key = option.id || option.name;
                          if (option?.canCreateLocation && structureCreationPermission) {
                            return (
                              <LiveSearchListItem key={key} {...props}>
                                <LocationSearchItem data={option} canCreateLocation={true} />
                              </LiveSearchListItem>
                            );
                          }

                          return (
                            <LiveSearchListItem key={key} {...props}>
                              <LocationSearchItem data={option} />
                            </LiveSearchListItem>
                          );
                        }}
                        onChange={async (locationNodeData: LocationNodeData) => {
                          if (locationNodeData?.canCreateLocation) {
                            setLocationText(locationNodeData.name);
                            setOpenLocationCreateUpdatePopup(true);
                          }
                          
                          let id;
                          if (locationNodeData?.isPartSearch) {
                            setData(prev => ({
                              ...prev,
                              reportedSerialNumberPartId: locationNodeData?.id,
                              reportedSerialNumber: locationNodeData?.name
                            }));
                            setSerialPartType(locationNodeData?.partType);
                            id = locationNodeData?.locationId;
                            isRuleExist(selectedRules, ServiceCreationRuleTypes.SerialNumber) && setSerialNoHelperText('');
                            locationNodeData?.locationId === 0 && updateClientHierarchyForSiblingWithoutParent(locationNodeData?.partType as NodePartTypeData);
                          } else {
                            id = locationNodeData?.id;
                          }

                          const subLocationsResult = id && id !== 0 && (await subLocationsForCreateServicesQuery.mutateAsync(id));
                          id !== 0 && setLocationId(id);

                          if (subLocationsResult && subLocationsResult.length > 0) {
                            const main = subLocationsResult.find(subLocation => subLocation.isMain);
                            const sub = subLocationsResult.find(subLocation => !subLocation.isMain && subLocation.isSelected);

                            //if we have a main node and a selected subLocation, select them by default
                            if (main && sub) {
                              setMainLocationNode(main);
                              setSubLocationNode(sub);
                              setLocationText(`${main.name || ''} ${(main.name && sub.name) ? '/' : ''} ${sub.name || ''}`.trim());
                            }
                            else {
                              const subLocationsOnly = subLocationsResult.filter(subLocation => !subLocation.isMain);
                              if (main?.isSelected && subLocationsOnly.length === 0) {
                                setMainLocationNode(main);
                                setSubLocationNode(undefined);
                                setLocationText(main.name);
                              }
                              else {
                                setLocationNodeOptions(subLocationsResult);
                              }
                            }

                            setLocationSearchError(false);
                          } else {
                            setLocationNodeOptions(undefined);
                            setMainLocationNode(undefined);
                            setSubLocationNode(undefined);
                            setLocationSearchError(true);
                            // resetting form when location is cleared
                            setSelectedClientId(0);
                            setSelectedContractId(0);
                            setSelectedBrandId(0);
                            setParentId('');
                            setBrandId('0');
                            setServiceTypeCode('');
                            setData(SAMPLE_SERVICE);                            
                            getStructuresByTypeCategoryCodeAndTypeCode.data && setClients(
                              getStructuresByTypeCategoryCodeAndTypeCode.data.map(item => ({ value: item.id.toString(), label: item.name, disabled: !item.isActive }))
                            );
                          }
                        }}
                        onApiInvoke={async (name) => {
                          let results: any[] = [];

                          if (!data.reportedSerialNumber) {
                            const hasCode = getHasCodeForNodeSearch(name);
                            if (!name.toLowerCase().startsWith(SearchCode.S_HASH) && defaultServiceTargetOption === ServiceTargetOptions.AnyStructureLocation || serviceTargetOptions?.some(targetOption => targetOption === ServiceTargetOptions.AnyStructureLocation) && name.toUpperCase().startsWith(SearchCode.AM_LID)) {
                              results = await searchNodeByParentId.mutateAsync({
                                name,
                                hasCode: hasCode
                              });
                            } else if (!name.toUpperCase().startsWith(SearchCode.AM_LID) && defaultServiceTargetOption === ServiceTargetOptions.SerialNumberUnderClientStructure || serviceTargetOptions?.some(targetOption => targetOption === ServiceTargetOptions.SerialNumberUnderClientStructure) && name.toLowerCase().startsWith(SearchCode.S_HASH)) {
                              results = await searchNodeWithParts.mutateAsync({
                                name: name.replace('#', '%23'),
                                isInClientHierarchy: true,
                                clientId: parseInt(clientIdSelected) || null
                              });
                            } else if (!name.toUpperCase().startsWith(SearchCode.AM_LID) && defaultServiceTargetOption === ServiceTargetOptions.SerialNumberAtAnyLocation || serviceTargetOptions?.some(targetOption => targetOption === ServiceTargetOptions.SerialNumberAtAnyLocation) && name.toLowerCase().startsWith(SearchCode.S_HASH)) {
                              results = await searchNodeWithParts.mutateAsync({
                                name: name.replace('#', '%23'),
                                isInClientHierarchy: !!clientIdSelected,
                                clientId: parseInt(clientIdSelected) || null
                              });
                            } else if (!name.toLowerCase().startsWith(SearchCode.S_HASH) && defaultServiceTargetOption === ServiceTargetOptions.StructureLocationUnderClient || serviceTargetOptions?.some(targetOption => targetOption === ServiceTargetOptions.StructureLocationUnderClient) && name.toUpperCase().startsWith(SearchCode.AM_LID)) {
                              results = await searchNodeByParentId.mutateAsync({
                                selectedParentId: brandId !== '0' ? brandId : parentId,
                                name,
                                hasCode: hasCode
                              });
                            } else {
                              results = await searchNodeByParentId.mutateAsync({
                                selectedParentId: brandId !== '0' ? brandId : parentId,
                                name,
                                hasCode: hasCode
                              });
                            }
                          }
                          else {
                            const hasCode = getHasCodeForNodeSearch(name);

                            if(isAtAnyLocation()) {
                              results = await searchNodeByParentId.mutateAsync({
                                name,
                                hasCode: hasCode
                              });
                            }
                            results = await searchNodeByParentId.mutateAsync({
                              selectedParentId: brandId !== '0' ? brandId : parentId,
                              name,
                              hasCode: hasCode
                            });
                          }

                          if (results.length === 0 && structureCreationPermission) {
                            results.push({
                              id: 0,
                              name: name,
                              code: '',
                              nodeTypeName: '',
                              canCreateLocation: true
                            });
                          }

                          return results;
                        }}
                        isError={locationSearchError}
                        helperText={locationHelperText}
                        size={SizeProps.Medium}
                      />
                    }
                  </Grid>
                  <Grid size={{ xs: 2 }}>
                    <StyledEditLocationBox>
                      <StyledButton 
                        fullWidth
                        disabled={!mainLocationNode?.name || !structureUpdatePermission}
                        variant={ButtonVariantProps.Secondary}
                        onClick={() => setOpenLocationCreateUpdatePopup(true)}
                      > 
                      Edit Location
                      </StyledButton>
                    </StyledEditLocationBox>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <ClientContractBrandPanel
                      isNew={true}
                      error={!!clientHelperText}
                      disableClient={!isAdvancedMode && !mainLocationNode}
                      onChangeNodeType={onChangeNodeType}
                      clients={clients}
                      contracts={contracts}
                      brands={brands}
                      clientId={selectedClientId}
                      contractId={selectedContractId}
                      brandId={selectedBrandId}
                      disableContract={contracts?.length === 1}
                      disableBrand={brands?.length === 1} 
                      onParentSelect={(nodeId) => {
                        setBrandId(nodeId.toString());
                        isClientEmpty(nodeId.toString()) || setClientHelperText('');
                      }}
                      locationSearchEnabled={false}
                      pageType="add-service"
                    />
                  </Grid>
                  {isAdvancedMode ? 
                    <>
                      <Grid size={{ xs: 12 }}>
                        <StyledSelectMenu
                          id="service-type-select"
                          required={true}
                          label="Service Type*"
                          isFocused={focusField === 'serviceTypeCode'}
                          selectedValue={data.serviceTypeCode}
                          onChange={(value) => {
                            setServiceTypeCode(value);
                            setData(prev => ({ ...prev, serviceTypeCode: value }));
                          }}
                          items={serviceTypes(serviceCommonTypes.serviceTypes)}
                          validate={validate}
                          optionalLabelEnabled={true}
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 12,
                          md: 6
                        }}>
                        <StyledSelectMenu
                          id="action-type-select"
                          required={true}
                          label="Action Type*"
                          isFocused={focusField === 'actionTypeCode'}
                          selectedValue={data.actionTypeCode}
                          onChange={(value) => {
                            setData(prev => ({ ...prev, actionTypeCode: value }));
                          }}
                          items={actionTypes(serviceCommonTypes.actionTypes)}
                          validate={validate}
                          optionalLabelEnabled={true}
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 12,
                          md: 6
                        }}>
                        <StyledSelectMenu
                          id="process-type-select"
                          required={true}
                          label="Process Type*"
                          isFocused={focusField === 'processTypeCode'}
                          selectedValue={data.processTypeCode}
                          onChange={(value) => {
                            setData(prev => ({ ...prev, processTypeCode: value }));
                          }}
                          items={processTypes(serviceCommonTypes.processTypes)}
                          validate={validate}
                          optionalLabelEnabled={true}
                        />
                      </Grid>
                    </> 
                    : 
                    <Grid size={{ xs: 12 }}>
                      <StyledSelectMenu
                        id="service-mapping-select"
                        required={true}
                        label={serviceTypeMappingData.length ? 'Service Type Mapping*' : 'No Service Type Mappings Available'}
                        selectedValue={data.serviceTypeMapping}
                        onChange={(value) => {
                          const serviceTypeMapping = serviceTypeMappingData.find((data) => data.id === value)?.value;
                          setServiceTypeCode(serviceTypeMapping?.selectedServiceType);
                          setData(prev => ({ ...prev, serviceTypeMapping: value, serviceTypeCode: serviceTypeMapping?.selectedServiceType, actionTypeCode: serviceTypeMapping?.selectedActionType, processTypeCode: serviceTypeMapping?.selectedProcessType }));
                        }}
                        items={serviceTypeMappings(serviceTypeMappingData)}
                        disabled={!serviceTypeMappingData.length || (!mainLocationNode && !parentId)}
                        validate={validate}
                        optionalLabelEnabled={true}
                      />
                    </Grid> 
                  }
                  <Grid size={{ xs: 12 }}>
                    <PrioritySelectMenu
                      id="priority-type-select"
                      label="Priority"
                      isFocused={focusField === 'priorityCode'}
                      value={data.priorityCode}
                      onChange={(value: string) => {
                        setData(prev => ({ ...prev, priorityCode: value }));
                      }}
                      items={priorityTypes(nodeAttributes)}
                      validate={validate}
                      optionalLabelEnabled={true}
                      disabled={!parentId || !serviceTypeCode}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <TextField
                      required={isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference1)}
                      fullWidth
                      label="Reference No 1"
                      inputRef={(element: HTMLElement) => { focusRef.current['referenceNo1' as keyof typeof focusRef] = element; }}
                      value={data.referenceNo1}
                      onChange={(value) => {
                        setData(prev => ({ ...prev, referenceNo1: value }));
                        if (isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference1)) {
                          setReference1HelperText(emptyFieldValidation(value, ServiceCreationRuleTypes.Reference1));
                        }
                      }}
                      helperText={reference1HelperText}
                      error={!!reference1HelperText}
                      disabled={!isAdvancedMode && !data.serviceTypeMapping}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <TextField
                      required={isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference2)}
                      fullWidth
                      label="Reference No 2"
                      inputRef={(element: HTMLElement) => { focusRef.current['referenceNo2' as keyof typeof focusRef] = element; }}
                      value={data.referenceNo2}
                      onChange={(value) => {
                        setData(prev => ({ ...prev, referenceNo2: value }));
                        if (isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference2)) {
                          setReference2HelperText(emptyFieldValidation(value, ServiceCreationRuleTypes.Reference2));
                        }
                      }}
                      helperText={reference2HelperText}
                      error={!!reference2HelperText}
                      disabled={!isAdvancedMode && !data.serviceTypeMapping}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <TextField
                      required={isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference3)}
                      fullWidth
                      label="Reference No 3"
                      inputRef={(element: HTMLElement) => { focusRef.current['referenceNo3' as keyof typeof focusRef] = element; }}
                      value={data.referenceNo3}
                      onChange={(value) => {
                        setData(prev => ({ ...prev, referenceNo3: value }));
                        if (isRuleExist(selectedRules, ServiceCreationRuleTypes.Reference3)) {
                          setReference3HelperText(emptyFieldValidation(value, ServiceCreationRuleTypes.Reference3));
                        }
                      }}
                      helperText={reference3HelperText}
                      error={!!reference3HelperText}
                      disabled={!isAdvancedMode && !data.serviceTypeMapping}
                    />
                  </Grid>
                  {isAdvancedMode && (
                    <>
                      <Grid size={{ xs: 12 }}>
                        <Tags
                          allTags={allTags}
                          inputRef={(element: HTMLElement) => { focusRef.current['tags' as keyof typeof focusRef] = element; }}
                          selectedTags={selectedTags}
                          setSeletedTags={onChangeSelectedTags}
                          newTags={newTags}
                          setNewTags={onChangeNewTags}
                        />
                      </Grid>
                      {userAllocationPermission && (
                        <Grid
                          size={{
                            xs: 12,
                            md: 6
                          }}>
                          <LiveSearchBox
                            title="User/Depot Allocation"
                            timeOffset={400}
                            value={data.userDepotAllocation ?? ''}
                            onClearValue={() => setData({ ...data, userDepotAllocation: '', userDepotAllocationNodeId: undefined })}
                            defaultSuggetions={suggestedNodesFromAllocation}
                            renderItem={(props: any, option: any) => {
                              return (
                                <StyledBox heighlightBackground={option.isSuggestedNode}>
                                  <LiveSearchListItem {...props}>
                                    <LocationSearchItem data={option} />
                                  </LiveSearchListItem>
                                </StyledBox>
                              );
                            }}
                            onChange={async (obj: LocationNodeData) => setData({ ...data, userDepotAllocation: obj.name, userDepotAllocationNodeId: obj.id })} //node: obj
                            onApiInvoke={async (obj) => {
                              const searcedhAllocations = await searchAllocationNodes.mutateAsync(obj);
                              if (suggestedNodesFromAllocation && suggestedNodesFromAllocation.length > 0) {
                                const newListWithoutDuplicates = searcedhAllocations.filter(item => {
                                  return !suggestedNodesFromAllocation.some(suggestedNode => suggestedNode.id == item.id);
                                });

                                return newListWithoutDuplicates;
                              }
                              return searcedhAllocations;
                            }}
                          />
                        </Grid>
                      )}
                      <Grid
                        size={{
                          xs: 12,
                          md: 6
                        }}>
                        <DateTimePicker
                          label="Scheduled Date/Time"
                          disablePast={true}
                          isFocused={focusField === 'scheduledAt'}
                          value={data.scheduledAt}
                          onChange={(value) => {
                            setData(prev => ({ ...prev, scheduledAt: value }));
                          }}
                        />
                      </Grid>
                    </>
                  )}
                  {!isAdvancedMode && notesComponent}
                </Grid>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Grid container spacing={GRID_SPACING}>
                  {isAdvancedMode && notesComponent}
                  <Grid size={{ xs: 12 }}>
                    <LiveSearchBox
                      isRequired={isRuleExist(selectedRules, ServiceCreationRuleTypes.SerialNumber)}
                      isError={!!serialNoHelperText}
                      disabled={!isAdvancedMode && !data.serviceTypeMapping}
                      helperText={serialNoHelperText}
                      title="Reported Serial Number"
                      timeOffset={400}
                      inputRef={(element: HTMLElement) => { focusRef.current['reportedSerialNumber' as keyof typeof focusRef] = element; }}
                      value={data.reportedSerialNumber || ''}
                      onClearValue={() => {
                        setData(prev => ({
                          ...prev,
                          reportedSerialNumberPartId: NaN,
                          reportedSerialNumber: ''
                        }));
                      }}
                      renderItem={(props: any, option: any) => {
                        return (
                          <LiveSearchListItem {...props}>
                            <LocationSearchItem data={option} />
                          </LiveSearchListItem>
                        );
                      }}
                      onChange={(serialData: LocationNodeData) => {
                        setData(prev => ({
                          ...prev,
                          reportedSerialNumberPartId: serialData?.id,
                          reportedSerialNumber: serialData?.name
                        }));
                        if (isRuleExist(selectedRules, ServiceCreationRuleTypes.SerialNumber)) {
                          setSerialNoHelperText(serialData ? emptyFieldValidation(serialData.name, ServiceCreationRuleTypes.SerialNumber) : `${ServiceCreationRuleTypes.SerialNumber} ${EMPTY_FIELD_MESSAGE}`);
                        }
                      }}
                      onApiInvoke={async (name: string) => {
                        return await searchParts.mutateAsync({ name });
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <StyledSelectMenu
                      id="model-type-select"
                      required={isRuleExist(selectedRules, ServiceCreationRuleTypes.Model)}
                      label="Reported Model"
                      isFocused={focusField === 'reportedModel'}
                      selectedValue={data.reportedModel}
                      onChange={(value) => {
                        setData(prev => ({ ...prev, reportedModel: value }));
                        if (isRuleExist(selectedRules, ServiceCreationRuleTypes.Model)) {
                          setValidate(true);
                        } else {
                          setValidate(false);
                        }
                      }}
                      items={reportedModelTypes(nodeAttributes)}
                      disabled={!parentId || !serviceTypeCode}
                      validate={isRuleExist(selectedRules, ServiceCreationRuleTypes.Model) && data.reportedModel === '-1'}
                      optionalLabelEnabled={true}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <StyledSelectMenu
                      id="fault-type-select"
                      required={isRuleExist(selectedRules, ServiceCreationRuleTypes.FaultAndDescription)}
                      label="Reported Fault and Description"
                      isFocused={focusField === 'reportedFaultAndDescription'}
                      selectedValue={data.reportedFaultAndDescription}
                      onChange={(value) => {
                        setData(prev => ({ ...prev, reportedFaultAndDescription: value }));
                        if (isRuleExist(selectedRules, ServiceCreationRuleTypes.FaultAndDescription)) {
                          setValidate(true);
                        } else {
                          setValidate(false);
                        }
                      }}
                      items={reportedFaultTypes(nodeAttributes)}
                      disabled={!parentId || !serviceTypeCode || !data.actionTypeCode || data.actionTypeCode === '-1'}
                      validate={isRuleExist(selectedRules, ServiceCreationRuleTypes.FaultAndDescription) && data.reportedFaultAndDescription === '-1'}
                      optionalLabelEnabled={true}
                    />
                  </Grid>
                  {data.isMultipleCreationEnabled && 
                    <Grid>
                      <SwitchGrid size={{ xs: 12 }} marginLeft={2}>
                        <FormControlLabel
                          control={
                            <Switch
                              color={ColorProps.Success}
                              onChange={(event) => setMultiServiceCreationFieldEnabled(event.target.checked)}
                              checked={multiServiceCreationFieldEnabled}
                            />
                          }
                          label="Create Service on all Children"
                          labelPlacement={FormControlLabelPlacementProps.End} />
                      </SwitchGrid>
                      <Grid size={{ xs: 12 }} marginLeft={2}>
                        <Typography>{childSummary}</Typography>
                      </Grid>
                    </Grid>
                  }
                  {isAdvancedMode && (
                    <Grid size={{ xs: 12 }}>
                      <ServiceContactLayout
                        isService={true}
                        contactFormData={contactData}
                        editContactFormData={editContactData}
                        contactDataset={structureContactDataset ? structureContactDataset : []}
                        onContactChange={(value) => {
                          setContactData(value);
                        }}
                        onClickContactEdit={(event, index) => {
                          const selectedValue: any = structureContactDataset.splice(index, 1);
                          setStructureContactDataset(structureContactDataset);
                          setEditContactData(selectedValue[0]);
                        }}
                        onClickContactDelete={(event, index) => {
                          const updatedData = [...structureContactDataset];
                          if (index > -1) updatedData.splice(index, 1);
                          setStructureContactDataset(updatedData);
                        }}
                        handleContactCreate={(index) => {
                          if (index > -1) contactData[index].isActive = true;
                          setStructureContactDataset([...structureContactDataset, contactData[index]]);
                          contactData.splice(index, 1);
                          setContactData([...contactData]);
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleClose}
                message="Service Added!"
                action={
                  <Button variant={ButtonVariantProps.Primary} color={ButtonColorProps.Secondary} onClick={handleClose}>
                    X
                  </Button>
                }
              />
            </Grid>
            <Grid container>
              <Grid size={{ xs: 12 }}>
                <Stack alignItems="flex-end">
                  {isInteractionSubmiting ?
                    <Loader />
                    :
                    <Button
                      variant={ButtonVariantProps.Primary}
                      onClick={handleServiceCreate}
                      disabled={
                        !hasByPassedFields(interceptedFormsObject as InterceptedFormsObject, getServiceObject(), getUserPermissionsQuery.data || []) ||
                      blockInteractionAvailable(getUserPermissionsQuery.data || [], interceptedFormsObject?.forms || [])
                      }
                    >
                      Save
                    </Button>
                  }
                </Stack>
              </Grid>
              {blockInteractionAvailable(getUserPermissionsQuery.data || [], interceptedFormsObject?.forms || []) &&
                <Grid size={{ xs: 12 }} textAlign="end">
                  <Typography color={COLORS.Red}>
                    {getBlockInteractionMessage(interceptedFormsObject?.forms || [])}
                  </Typography>
                </Grid>
              }
            </Grid>
          </ServiceBox>
        </Grid>
      </Grid>
      <InteractionsContainer>
        {interceptedFormsObject?.forms?.map((form, index) =>
          <FormInterceptor
            key={index}
            service={getServiceObject()}
            value={form}
            onChange={(editedForms) => {
              if (repeatableFormData.templateId === 0 && repeatableFormData.templateTitle === '') {
                const updatedForms = interceptedFormsObject?.forms.map((formData) => formData.id === editedForms.id ? editedForms : formData);
                setInterceptedFormsObject({
                  ...interceptedFormsObject,
                  forms: updatedForms
                });
              }
            }}
            onDamagedPartSelect={(Objectkey: string, objectValue: FormData | number | MerchantDamagePartSerialMenuData) => {
              setMerchantDamageObjectParcer(prevState => ({ ...prevState, [Objectkey]: objectValue }));
            }}
            onAddForm={(templateId, templateTitle) => setRepeatableFormData({ templateId, templateTitle })}
            isRepeatable={getFormIsRepeatableStatus(form, interceptedFormsObject.forms)}
            forms={interceptedFormsObject.forms}
          />
        )}
      </InteractionsContainer>
      <Modal
        open={openLocationCreateUpdatePopup}
        onClose={() => setOpenLocationCreateUpdatePopup(false)}
        sx={{
          overflowY: 'auto',
        }}
      >
        <StructureEditContainer sx={{ width: '80%', maxWidth: '80%' }}>
          <CloseIcon handleClose={() => setOpenLocationCreateUpdatePopup(false)} />
          <ContentBox>
            <AddEditStructureLocationWrapper 
              pageType="service"
              structureId={subLocationNode?.name ? subLocationNode?.id.toString() : mainLocationNode?.id.toString()}
              locationType={subLocationNode?.name ? 'sub-location' : 'main-location'}
              locationText={locationText}
              mainLocationNode={mainLocationNode}
              defaultClientId={selectedClientId ? selectedClientId.toString() : ''}
              defaultContractId={selectedContractId ? selectedContractId.toString() : ''}
              defaultBrandId={selectedBrandId ? selectedBrandId.toString() : ''}
              setMainLocationNode={setMainLocationNode}
              onSetCreatedLocation={onSetCreatedLocation}
              setOpenLocationCreateUpdatePopup={setOpenLocationCreateUpdatePopup}
              setSubLocationNode={setSubLocationNode}
              setLocationText={setLocationText}
            />
          </ContentBox>
        </StructureEditContainer>
      </Modal>
    </PageContainer>
  );
};
